import {
  dateAFormatoGlobal,
  fechaConHora,
  fechaEnFormatoHHMM,
  HHMMaMinutos,
  min2Digitos,
} from "src/utils/fechas";
import {
  Corte,
  CorteProceso,
  FiltrosHistorial,
  FiltrosReclamos,
  ReclamoCreacionConProcesadaAPI,
  ReclamoCreacionSinProcesadaAPI,
  ReclamoProceso,
  ReclamoProcesoAPI,
  RegistroReclamoAPI,
} from "src/utils/interfaces";

/**************************************************************
 * LISTA DE CONVERSIONES DE DATOS LOCALES A DATOS PARA LA API *
 **************************************************************/

/*****/
/* 1 */
/*****/

/** HISTORIAL */

export function crearQueryParamHistorial(filtros: FiltrosHistorial) {
  const codigoPuntoServicio = filtros.puntoDeServicio?.codigo;
  let fechaInicio, fechaFin;
  let params = "";

  if (filtros.fechaInicio) fechaInicio = fechaAQueryParam(filtros.fechaInicio);
  if (filtros.fechaFin) fechaFin = fechaAQueryParam(filtros.fechaFin);

  params = adjuntarComoQueryParam(params, "fecha_desde", fechaInicio);
  params = adjuntarComoQueryParam(params, "fecha_hasta", fechaFin);
  params = adjuntarComoQueryParam(params, "codigo_ps", codigoPuntoServicio);

  return params;
}

/** HISTORIAL - FIN */

/*****/
/* 2 */
/*****/

/** RECLAMOS */

// cambia la estructura de reclamo al esperado por la API para crear reclamos
export function formateoCrearReclamoConHoraProcesada(
  reclamo: ReclamoProceso
): ReclamoCreacionConProcesadaAPI {
  const corte = reclamo.cortes[0];
  const horaEntrada = fechaEnFormatoHHMM(corte.horaEntrada);
  const horaSalida = fechaEnFormatoHHMM(corte.horaSalida);

  return {
    horas: {
      h_entrada_1: horaEntrada,
      h_salida_1: horaSalida,
    },
    reclamo: reclamo.reclamo,
    // Comentario libre del operario (aplica especialmente cuando el motivo es "Otros").
    // Este campo se usa para construir el detalle de la incidencia cuando el reclamo
    // se deriva a EMSYS (post-cierre / mes cerrado / fuera de rango).
    comentario: reclamo.comentario,
    origen: "APP",
  };
}

// cambia la estructura de reclamo al esperado por la API para crear reclamos
export function formateoCrearReclamoSinHoraProcesada(
  reclamo: ReclamoProceso
): ReclamoCreacionSinProcesadaAPI | undefined {
  const corte = reclamo.cortes[0];
  const horaEntrada = fechaEnFormatoHHMM(corte.horaEntrada);
  const horaSalida = fechaEnFormatoHHMM(corte.horaSalida);
  const fecha = dateAFormatoGlobal(reclamo.fechaInicio as Date);

  return {
    reclamo: reclamo.reclamo,
    // Comentario libre del operario (aplica especialmente cuando el motivo es "Otros").
    // Se envía a la API para poder adjuntarlo a la incidencia cuando corresponda.
    comentario: reclamo.comentario,
    horas: {
      h_entrada_1: horaEntrada,
      h_salida_1: horaSalida,
    },
    origen: "APP",
    ps_codigo: reclamo.codigoPuntoServicio as string,
    ps_nombre: reclamo.nombrePuntoServicio as string,
    fecha: fecha,
  };
}

// cambia la estructura de reclamo al esperado por la API para gestionar reclamos
export function formateoProcesarReclamo(reclamo: ReclamoProceso) {
  const datosReclamo = {} as any;

  reclamo.cortes.forEach((corte, idx) => {
    if (corte) {
      const horaEntrada = fechaEnFormatoHHMM(corte.horaEntrada);
      const horaSalida = fechaEnFormatoHHMM(corte.horaSalida);

      const sufijo = idx > 0 ? `_${idx + 1}` : "";

      const keyEntrada = `he_aprobado${sufijo}`;
      const keySalida = `hs_aprobado${sufijo}`;

      datosReclamo[keyEntrada] = horaEntrada;
      datosReclamo[keySalida] = horaSalida;
    }
  });

  datosReclamo.observacion = reclamo.reclamo;
  datosReclamo.respuesta = reclamo.accion;
  //datosReclamo.aceptar = reclamo.accion === "aceptar";

  return datosReclamo as ReclamoProcesoAPI;
}

// carga los parametros de filtros para listar reclamos
export function crearQueryParamReclamos(
  pagina: number,
  filtros: FiltrosReclamos
) {
  const codigoPuntoServicio = filtros.puntoDeServicio?.codigo;
  let fechaInicio, fechaFin;
  let params = "";

  if (filtros.fechaInicio) fechaInicio = fechaAQueryParam(filtros.fechaInicio);
  if (filtros.fechaFin) fechaFin = fechaAQueryParam(filtros.fechaFin);

  params = adjuntarComoQueryParam(params, "pagina_n", pagina);
  params = adjuntarComoQueryParam(params, "fecha_desde", fechaInicio);
  params = adjuntarComoQueryParam(params, "fecha_hasta", fechaFin);
  params = adjuntarComoQueryParam(params, "codigo_ps", codigoPuntoServicio);

  return params;
}

// convierte la estructura de cortes de una entidad Reclamo de la API
// a la local, usando array de cortes
export function estructurarCortes(
  registro: RegistroReclamoAPI
): CorteProceso[] {
  const cortes: CorteProceso[] = [];
  const fecha = new Date(registro.fecha);
  const hayCortes = true;

  // if (registro.estado === "INICIADO") {
  //   agregarCorte(cortes, fecha, registro.he_nueva, registro.hs_nueva);
  // } else hayCortes = true;

  if (hayCortes && registro.he_1 && registro.hs_1)
    agregarCorte(cortes, fecha, registro.he_1, registro.hs_1);

  if (hayCortes && registro.he_2 && registro.hs_2)
    agregarCorte(cortes, fecha, registro.he_2, registro.hs_2);

  if (hayCortes && registro.he_3 && registro.hs_3)
    agregarCorte(cortes, fecha, registro.he_3, registro.hs_3);

  return cortes;
}

// agrega un corte del reclamo de la API a un corte de la estructura local de Reclamo
export function agregarCorte(
  cortes: Corte[],
  fecha: Date,
  horaEntrada: string,
  horaSalida: string
) {
  const entrada = fechaConHora(fecha, HHMMaMinutos(horaEntrada));
  const salida = fechaConHora(fecha, HHMMaMinutos(horaSalida));

  cortes.push({ horaEntrada: entrada, horaSalida: salida });
}

/** RECLAMOS - FIN */

/*****/
/* 3 */
/*****/

/** GENERAL */

export function adjuntarComoQueryParam(
  paramsBase: string,
  clave: string,
  valor: any
) {
  const nuevoFiltro = clave && valor ? `${clave}=${valor}` : "";

  if (!nuevoFiltro) return paramsBase;

  if (paramsBase) return `${paramsBase}&${nuevoFiltro}`;
  else return `?${nuevoFiltro}`;
}

export function fechaAQueryParam(fecha: Date) {
  const año = fecha.getUTCFullYear();
  const mes = fecha.getUTCMonth() + 1;
  const dia = fecha.getUTCDate();

  return `${año}-${min2Digitos(mes)}-${min2Digitos(dia)}`;
}

/** GENERAL - FIN */




